import { Alert, Radio as MantineRadio } from '@mantine/core';
import { CircularProgress, Radio, RadioGroup } from '@mui/material';
import { EditIcon } from 'assets/Icons';
import { Spacer } from 'components/atoms';
import { StyledFormLabel } from 'components/atoms/InputField/style';
import { EditAddressModal } from 'components/molecules';
import { NewUseUpdatedCart } from 'components/molecules/Cart/hooks';
import FreeDeliveryModal from 'components/molecules/FreeDeliveryModal';
import Fuse from 'fuse.js';
import renderIcon from 'icon';
import { uniqBy } from 'lodash';
import QUERY_KEYS from 'network/config/queryKeys';
import { getAllAddress } from 'network/services/address';
import {
  getDeliveryRates,
  getShipBubbleCategories,
  getShipBubbleDimensions,
  validateAddressDeliveryCallback
} from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { IoInformationCircleSharp, IoLocationOutline } from 'react-icons/io5';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { checkoutDetailsAtom } from 'store/atoms/checkoutAtom';
import checkoutVariablesAtom from 'store/atoms/checkoutVariables';
import { contactState } from 'store/atoms/contactAtom';
import { profileAtom } from 'store/atoms/profileAtom';
import { AddressResponse } from 'TYPES/api.body';

interface ShipBubbleCategory {
  category_id: string;
  category: string;
}

const CATEGORY_MAPPING: Record<string, string[]> = {
  'Electronics & Appliances': ['Electronics', 'Electronic gadgets'],
  'Fashion & Apparel': ['Fashion wears', 'Accessories'],
  'Beauty, Pharmacy, Health & Personal Care': ['Accessories'],
  'Groceries, Groceries & Everyday Essentials': ['Food'],
  'Home, Furniture & Living': ['Accessories'],
  'Automotive & Industrial': ['Accessories'],
  'Computers, Office & Stationery': ['Electronics', 'Electronic gadgets'],
  'Baby, Kids & Toys': ['Accessories'],
  'Sports, Fitness & Outdoors': ['Accessories'],
  'Books, Media & Education': ['Accessories'],
  'Phones & Accessories': ['Electronics', 'Electronic gadgets', 'Accessories'],
  'Real Estate & Property Listings': ['Accessories'],
  'Used, Refurbished & Clearance Items': ['Accessories'],
  'Agriculture & Farming': ['Food'],
  'Art, Crafts & Handmade Items': ['Accessories'],
  'Services & Digital Products': ['Accessories'],
  'Building, Construction & Materials': ['Accessories'],
  'Energy & Utilities': ['Accessories'],
  'Pets & Animals': ['Food', 'Accessories'],
  'Religious & Cultural Items': ['Accessories'],
  'Events & Occasions': ['Accessories'],
  'Lottery & Gaming': ['Accessories'],
  'Jewelry': ['Jewelry']
};

const DeliveryDetails = () => {
  const go = useNavigate();
  const { orderId } = useParams();
  const { pathname } = useLocation();
  const profile = useRecoilValue(profileAtom);
  const [addressInfo, setAddressInfo] = useRecoilState(contactState);
  const serverCart = NewUseUpdatedCart()?.data;
  const cartOrder = (serverCart?.orders as any[])?.find(order => order?.locationId === Number(orderId));

  const [checkoutVariables, setCheckoutVariables] = useRecoilState(checkoutVariablesAtom);
  const [checkoutDetails, setCheckoutDetails] = useRecoilState(checkoutDetailsAtom);

  const [freeDeliveryModalOpen, setFreeDeliveryModalOpen] = React.useState(false);
  const [freeDeliveryData, setFreeDeliveryData] = React.useState<any>(null);

  const getProductDetailsString = (products: any[] = []) =>
    products.map(p => `${p.productName} x${p.quantity}`).join(', ');

  const {
    data: addressData,
    isLoading: addressLoading,
    refetch: refetchAddresses
  } = useQuery({
    queryFn: getAllAddress,
    queryKey: QUERY_KEYS.CUSTOMER_ADDRESSES,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    enabled: !!profile?.email
  });

  React.useEffect(() => {
    if (profile?.email) refetchAddresses();
  }, [pathname, profile?.email, refetchAddresses]);

  const calculateTotalWeight = (products: any[] = []) => {
    return products.reduce((acc: number, product: any) => {
      const weight = product.size && product.size > 0 ? product.size : 0.001;
      return acc + product.quantity * weight;
    }, 0);
  };

  const getPackageDimensions = (totalWeight: number, dimensions: any[] = []) => {
    const sorted = dimensions?.sort((a: any, b: any) => a.size - b.size);
    return sorted?.find((box: any) => box.size >= totalWeight) || sorted?.[sorted.length - 1];
  };

  const { data: shipBubbleCategories } = useQuery({
    queryFn: getShipBubbleCategories,
    queryKey: QUERY_KEYS.SHIPBUBBLE_CATEGORIES
  });

  const { data: shipBubbleDimensions } = useQuery({
    queryFn: getShipBubbleDimensions,
    queryKey: QUERY_KEYS.SHIPBUBBLE_DIMENSIONS
  });

  const getShipBubbleCategoryId = React.useCallback((customCategory: string): string => {
    if (!shipBubbleCategories || shipBubbleCategories.length === 0) return '2178251';

    const mapped = CATEGORY_MAPPING[customCategory];
    if (mapped) {
      for (const name of mapped) {
        const exact = shipBubbleCategories.find(
          (cat: ShipBubbleCategory) => cat.category.toLowerCase() === name.toLowerCase()
        );
        if (exact) return exact.category_id;
      }
      for (const name of mapped) {
        const partial = shipBubbleCategories.find(
          (cat: ShipBubbleCategory) => cat.category.toLowerCase().includes(name.toLowerCase())
        );
        if (partial) return partial.category_id;
      }
    }

    const fuse = new Fuse<ShipBubbleCategory>(shipBubbleCategories, {
      keys: ['category'],
      threshold: 0.4
    });
    const result = fuse.search(customCategory);
    return result.length > 0 ? result[0].item.category_id : '2178251';
  }, [shipBubbleCategories]);

  const packageDimension = React.useMemo(() => {
    if (!shipBubbleDimensions || !cartOrder?.products?.length) return null;
    const totalWeight = calculateTotalWeight(cartOrder.products);
    return getPackageDimensions(totalWeight, shipBubbleDimensions);
  }, [shipBubbleDimensions, cartOrder?.products]);

  const {
    mutateAsync: asyncGetDeliveryRates,
    data: deliveryRatesData,
    isLoading: deliveryRatesLoading
  } = useMutation(getDeliveryRates, {
    onSuccess: (data) => {
      const requestToken = data?.data?.data?.request_token;
      if (requestToken) {
        setCheckoutVariables(prev => ({ ...prev, request_token: requestToken }));
      }
    },
    onError: () => {
      enqueueSnackbar('Error fetching delivery rates', { variant: 'error' });
    }
  });

  const { mutateAsync: validateAddress, isLoading: validatingAddress } = useMutation({
    mutationFn: validateAddressDeliveryCallback
  });

  const carriers = React.useMemo(() => {
    return uniqBy(deliveryRatesData?.data?.data?.couriers || [], (c: any) => c.courier_id);
  }, [deliveryRatesData]);

  const addresses = React.useMemo(() => addressData?.data || [], [addressData?.data]);
  const defaultAddress = React.useMemo(() => addresses.find((a: any) => a.status === true), [addresses]);

  React.useEffect(() => {
    if (defaultAddress && checkoutDetails.addressId !== defaultAddress.id) {
      setCheckoutDetails(prev => ({ ...prev, addressId: defaultAddress.id }));
    }
  }, [defaultAddress, checkoutDetails.addressId, setCheckoutDetails]);

  // Proven working handleGetCarriers with address validation
  const handleGetCarriers = React.useCallback(async () => {
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    if (!profile?.email) {
      enqueueSnackbar('Please login to continue', { variant: 'error' });
      go('/login', { state: { from: pathname } });
      return;
    }

    if (!defaultAddress && !addressLoading) {
      enqueueSnackbar('No default address found: Please add a default delivery address', { variant: 'error' });
      return;
    }

    if (defaultAddress) {
      setCheckoutDetails(c => ({ ...c, addressId: defaultAddress.id }));
    }

    if (!shipBubbleCategories || !defaultAddress || !cartOrder?.vendorAddress || !packageDimension || !cartOrder?.products?.length) {
      return;
    }

    const categoryId = cartOrder.vendorCategory ? getShipBubbleCategoryId(cartOrder.vendorCategory) : '2178251';

    try {
      const senderAddressString = [cartOrder.vendorAddress.street, cartOrder.vendorAddress.city, cartOrder.vendorAddress.state, cartOrder.vendorAddress.country]
        .filter(Boolean).join(', ');
      const receiverAddressString = [defaultAddress.street, defaultAddress.city, defaultAddress.state, defaultAddress.country]
        .filter(Boolean).join(', ');

      const senderName = cartOrder.vendorName?.trim() || '';
      const senderPhone = cartOrder.vendorPhone?.trim() || '';
      const receiverName = `${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`.trim();
      const receiverEmail = profile?.email?.trim() || '';
      const receiverPhone = profile?.phoneNumber?.trim() || '';

      let senderAddressCode = '';
      let receiverAddressCode = '';

      // Validate receiver
      if (receiverName && receiverEmail && receiverPhone && receiverAddressString) {
        try {
          const res = await validateAddress({
            name: receiverName,
            email: receiverEmail,
            phone: receiverPhone,
            address: receiverAddressString
          });
          // Try both possible response structures (local vs production)
          receiverAddressCode = res?.data?.data?.address_code || res?.data?.address_code || String(defaultAddress.address_code || '');
        } catch (err) {
          receiverAddressCode = String(defaultAddress.address_code || '');
        }
      } else {
        receiverAddressCode = String(defaultAddress.address_code || '');
      }

      // Validate sender
      if (senderName && senderPhone && senderAddressString) {
        const formattedSenderName = senderName.includes(' ') ? senderName : `${senderName} Store`;
        try {
          const res = await validateAddress({
            name: formattedSenderName,
            email: 'info@madina.ng',
            phone: senderPhone,
            address: senderAddressString
          });
          // Try both possible response structures (local vs production)
          senderAddressCode = res?.data?.data?.address_code || res?.data?.address_code || '';
        } catch (err) {
          senderAddressCode = '';
        }
      }

      if (!senderAddressCode) {
        senderAddressCode = cartOrder.vendorAddress.address_code ? String(cartOrder.vendorAddress.address_code) : '';
      }

      if (!senderAddressCode || !receiverAddressCode) {
        enqueueSnackbar('Could not validate delivery addresses. Please try again.', { variant: 'error' });
        return;
      }

      await asyncGetDeliveryRates({
        sender_address_code: senderAddressCode,
        reciever_address_code: receiverAddressCode,
        pickup_date: formattedDate,
        category_id: categoryId,
        package_items: cartOrder.products.map((item: any) => ({
          name: item.productName,
          description: item.description && item.description.trim() !== '' ? item.description : 'Product on Madina',
          unit_weight: `${item.size && item.size > 0 ? item.size : 0.001}`,
          unit_amount: `${item.price}`,
          quantity: `${item.quantity}`
        })),
        service_type: 'pickup',
        delivery_instructions: 'this needs to be delivered',
        package_dimension: {
          length: packageDimension?.length ?? 12,
          width: packageDimension?.width ?? 30,
          height: packageDimension?.height ?? 11
        },
        ship_from_details: {
          name: cartOrder.vendorName ?? '',
          address: cartOrder.vendorAddress.street ?? '',
          city: cartOrder.vendorAddress.city ?? '',
          state: cartOrder.vendorAddress.state ?? '',
          country: cartOrder.vendorAddress.country ?? '',
          phone: cartOrder.vendorPhone ?? ''
        },
        ship_to_details: {
          name: receiverName,
          address: defaultAddress.street ?? '',
          city: defaultAddress.city ?? '',
          state: defaultAddress.state ?? '',
          country: defaultAddress.country ?? '',
          phone: profile?.phoneNumber ?? ''
        },
        package_amount: cartOrder.products.reduce((sum: number, item: any) => sum + item.price * item.quantity, 0),
        package_weight: calculateTotalWeight(cartOrder.products),
        items_details: cartOrder.products.map((item: any) => ({
          name: item.productName,
          quantity: item.quantity,
          weight: item.size || 0.001
        }))
      });
    } catch (error) {
      console.error('Error in handleGetCarriers:', error);
      enqueueSnackbar('Error getting delivery options', { variant: 'error' });
    }
  }, [
    profile, defaultAddress, addressLoading, cartOrder, shipBubbleCategories,
    packageDimension, getShipBubbleCategoryId, asyncGetDeliveryRates,
    validateAddress, go, pathname, setCheckoutDetails
  ]);

  React.useEffect(() => {
    if (checkoutDetails.deliveryMethod === 'homedelivery') {
      handleGetCarriers();
    }
  }, [checkoutDetails.deliveryMethod, handleGetCarriers]);

  React.useEffect(() => {
    if (checkoutDetails.deliveryMethod === 'homedelivery' && defaultAddress?.id) {
      handleGetCarriers();
    }
  }, [defaultAddress?.id, handleGetCarriers]);

  const getFilteredCarriers = React.useMemo(() => {
    if (!carriers.length) return [];
    if (carriers.length === 1) return carriers;

    const cheapest = carriers.reduce((a, b) => (a.total < b.total ? a : b));
    const fastest = carriers.reduce((a, b) => {
      return new Date(a.delivery_eta_time).getTime() < new Date(b.delivery_eta_time).getTime() ? a : b;
    });

    const unique = cheapest.courier_id === fastest.courier_id ? [cheapest] : [cheapest, fastest];
    return unique.sort((a, b) => a.total - b.total);
  }, [carriers]);

  const handleFreeDeliverySubmit = (data: { orderId: string; recipientName: string; recipientAddress: string; recipientPhone: string; productDetails: string }) => {
    const free_delivery_name = data.recipientName.trim();
    const free_delivery_location = data.recipientAddress.trim();
    const free_delivery_phone = data.recipientPhone.trim();

    setFreeDeliveryData({ free_delivery_name, free_delivery_location, free_delivery_phone });
    setCheckoutVariables(prev => ({
      ...prev,
      selectedCarrier: { total: 0, tag: 'free', courier_name: 'Free Delivery' }
    }));
    setCheckoutDetails(prev => ({
      ...prev,
      deliveryMethod: 'freedelivery',
      free_delivery_name,
      free_delivery_location,
      free_delivery_phone
    }));
    enqueueSnackbar('Free delivery details captured! Please proceed to place your order.', { variant: 'success' });
  };

  const showAddressModal = (item: AddressResponse) => {
    setAddressInfo({
      isEditAddressModal: true,
      ...item,
      id: item.id.toString(),
      city: item.city ?? ''
    });
  };

  const addNewAddress = () => {
    setAddressInfo({
      isEditAddressModal: true,
      id: '',
      lastname: '',
      firstname: '',
      street: '',
      city: '',
      state: '',
      country: '',
      phoneNumber: '',
      status: false
    });
  };

  return (
    <>
      <p className="sub-head">Delivery Method</p>

      <RadioGroup
        row
        value={checkoutDetails.deliveryMethod}
        onChange={e => {
          const value = e.currentTarget.value as any;
          setCheckoutDetails(curr => ({ ...curr, deliveryMethod: value }));
          if (value === 'freedelivery') setFreeDeliveryModalOpen(true);
        }}>
        <StyledFormLabel value="homedelivery" control={<Radio />} label="Home Delivery" />
        <Spacer width={34} />
        <StyledFormLabel value="pickup" control={<Radio />} label="Pick up" />
        <Spacer width={34} />
        <StyledFormLabel value="freedelivery" control={<Radio />} label="Free Delivery" />
      </RadioGroup>

      <FreeDeliveryModal
        opened={freeDeliveryModalOpen}
        onClose={() => setFreeDeliveryModalOpen(false)}
        orderId={orderId || ''}
        productDetails={getProductDetailsString(cartOrder?.products)}
        onSubmit={handleFreeDeliverySubmit}
      />

      {freeDeliveryData && (
        <div className="my-4 p-4 bg-green-50 border border-green-300 rounded-lg text-green-800 flex items-center gap-2">

          <span>Free Delivery selected for this order!</span>
        </div>
      )}

      {checkoutDetails.deliveryMethod === 'homedelivery' && (
        <>
          <div className="mt-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
              <p className="sub-head text-base sm:text-lg">Default Delivery Address</p>
              <button type="button" onClick={addNewAddress} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                + Add New Address
              </button>
            </div>

            {addressLoading ? (
              <div className="flex items-center justify-center py-8">
                <CircularProgress size={24} />
                <span className="ml-2 text-gray-600">Loading default address...</span>
              </div>
            ) : !defaultAddress ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <p className="text-gray-500 mb-3">No default delivery address found</p>
                <p className="text-sm text-gray-400 mb-4">Please add a default delivery address to continue</p>
                <button type="button" onClick={addNewAddress} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm">
                  Add Default Address
                </button>
              </div>
            ) : (
              <div className="border-2 border-blue-500 bg-blue-50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">{defaultAddress.street}</p>
                  </div>
                  <button type="button" className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm" onClick={() => showAddressModal(defaultAddress)}>
                    <EditIcon width={16} />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mb-4">
            <h4 className="font-semibold text-gray-900 mb-3">Select Delivery Service</h4>

            <MantineRadio.Group
              value={checkoutVariables.selectedCarrier?.courier_id}
              onChange={id => setCheckoutVariables(prev => ({
                ...prev,
                selectedCarrier: getFilteredCarriers.find(c => c.courier_id === id)
              }))}
              name="preferredCarrier">
              <div className="space-y-3">
                {deliveryRatesLoading || validatingAddress ? (
                  <div className="flex items-center justify-center py-8">
                    <CircularProgress size={24} />
                    <span className="ml-2 text-gray-600">Loading delivery options...</span>
                  </div>
                ) : getFilteredCarriers.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    <p>No delivery options available at the moment.</p>
                  </div>
                ) : (
                  getFilteredCarriers.map((c, i) => {
                    const isCheapest = c.total === Math.min(...carriers.map(car => car.total));
                    const isFastest = new Date(c.delivery_eta_time).getTime() === Math.min(...carriers.map(car => new Date(car.delivery_eta_time).getTime()));

                    return (
                      <div
                        key={i}
                        onClick={() => setCheckoutVariables(prev => ({ ...prev, selectedCarrier: c }))}
                        className={`w-full p-4 border-2 rounded-lg transition-all duration-200 cursor-pointer ${checkoutVariables.selectedCarrier?.courier_id === c.courier_id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <img src={c.courier_image ?? '/images/chowdeck.webp'} alt={c.courier_name} className="rounded-full w-10 h-10 border-2 border-gray-100" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-gray-900 truncate">{c.courier_name}</h5>
                                <div className="flex gap-1">
                                  {isCheapest && <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">Cheapest</span>}
                                  {isFastest && <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">Fastest</span>}
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 font-bold">Estimated delivery: {c.delivery_eta}</p>
                            </div>
                          </div>
                          <div className="text-lg font-bold text-gray-900">₦{c.total.toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </MantineRadio.Group>
          </div>
        </>
      )}

      {checkoutDetails.deliveryMethod === 'pickup' && cartOrder?.vendorAddress && (
        <Alert className="my-5 rounded-lg" icon={renderIcon(IoLocationOutline, { size: 24 })} color="#2F4DC4" title="Pickup Location">
          <div className="text-gray-700">
            <p className="font-medium">{cartOrder.vendorName}</p>
            <p>{cartOrder.vendorAddress.street}</p>
            <p>{cartOrder.vendorAddress.state}, {cartOrder.vendorAddress.country}</p>
            <p className="mt-2">Contact: {cartOrder.vendorPhone}</p>
          </div>
        </Alert>
      )}

      {checkoutDetails.deliveryMethod === 'homedelivery' && checkoutVariables.selectedCarrier && (
        <Alert
          className="my-5 rounded-lg"
          icon={renderIcon(IoInformationCircleSharp, { size: 24 })}
          color="#2F4DC4"
          title={`${checkoutVariables.selectedCarrier.delivery_eta || ''} Shipping Fee: ₦${checkoutVariables.selectedCarrier.total?.toFixed(2)}`}
        />
      )}

      {addressInfo.isEditAddressModal && <EditAddressModal />}
    </>
  );
};

export default DeliveryDetails;